import React, { useEffect, useState } from 'react'
import { InputItem, List } from 'antd-mobile'
import { FormattedMessage, useIntl } from 'react-intl'
import { getCookie } from '../../components/common/cookie'
import PickerComponent from '../../components/common/Picker'
import { useTextbookOrderAddress } from '../../services/hooks'
import { extractCnLabel, useGetChinaCities, useGetChinaProvinces } from '../../components/FormTools/SelectRegion'
import Loading from '../../components/Loading'
import { MAINLAND_ADDRESS_TYPE, textbooksRegions } from '../../components/common/utils'
import styles from './ShippingAddress.module.css'

const DEFAULT_ADDRESS_TYPE = 1

// 历史地址回填匹配：label 已由 SelectRegion 清洗为简体中文，历史值可能是原始混合串
// （"Hebei Province - 河北省 Héběi Shěng"）或后端存储的简体名，统一经 extractCnLabel 归一后比较
const containsCn = (label, name) => {
  const cleanName = extractCnLabel(name)
  if (!cleanName) return false
  return label.includes(cleanName)
}

export default function ShippingAddress({
  addressType,
  onRegionChange,
  address,
  setAddress,
  userName,
  setUserName,
  userMobile,
  setUserMobile,
  // 各字段行内报错文案（来自父组件，用户重新输入/选择时清除）
  mobileError,
  setMobileError,
  addressTypeError,
  setAddressTypeError,
  provinceError,
  setProvinceError,
  cityError,
  setCityError,
  addressError,
  setAddressError,
  userNameError,
  setUserNameError,
  // 由父组件按商品币种判定（CNY=大陆），不依赖 profile 加载时机
  isMainland,
  // 大陆省市：值传 PADI 参考接口原始 label，后端归一化后匹配运费档
  province,
  onProvinceChange,
  city,
  onCityChange,
}) {
  const { formatMessage } = useIntl()

  // 大陆用户仅可选中国大陆（value 与后端 textbooks_express_type 大陆类型一致）；非大陆隐藏大陆项
  const regionOptions = (textbooksRegions[getCookie('language')] || []).filter((region) =>
    isMainland ? region.value === MAINLAND_ADDRESS_TYPE : region.value !== MAINLAND_ADDRESS_TYPE
  )
  const isValidAddressType = (value) => regionOptions.some((region) => region.value === value)

  // 地区选择变化（含历史地址回填）统一走这里：清除地区行内报错后再通知父组件
  const handleRegionChange = (value) => {
    if (addressTypeError) setAddressTypeError('')
    onRegionChange(value)
  }

  // 省市选择器（大陆）：数据源 PADI Global 参考接口，选项固定简体中文（该功能仅面向中国大陆用户）；
  // 市区是收货地址组成部分、所有省市一律必选（产品决策 2026-08-17），运费档由后端按省市归一化
  const [provinceId, setProvinceId] = useState()
  const [cityId, setCityId] = useState()
  const [historyProvince, setHistoryProvince] = useState()
  const [historyCity, setHistoryCity] = useState()
  const { data: provinceOptions } = useGetChinaProvinces()
  const { data: cityOptions } = useGetChinaCities(provinceId)

  const { loading } = useTextbookOrderAddress({
    onSuccess: (data) => {
      if (isMainland) {
        setHistoryProvince(data.province)
        setHistoryCity(data.city)
        setUserMobile(data.userMobile)
        setUserName(data.userName)
        setAddress(data.address)
        handleRegionChange(MAINLAND_ADDRESS_TYPE)
      } else {
        // 历史地址类型仅当仍属于当前地区选项（大陆/非大陆）时才生效，否则回落默认值
        if (data.addressType && isValidAddressType(data.addressType)) {
          handleRegionChange(data.addressType)
          setUserMobile(data.userMobile)
          setUserName(data.userName)
          setAddress(data.address)
        } else {
          handleRegionChange(isMainland ? MAINLAND_ADDRESS_TYPE : DEFAULT_ADDRESS_TYPE)
        }
      }
    },
  })

  // 省回填依赖 PADI 选项（地址接口可能先于参考接口返回，需等 options 就绪后再回填）；
  // 干净中文名回匹配 PADI 选项（先繁转简），匹配不到则不预填、由用户重选
  useEffect(() => {
    if (!isMainland || !historyProvince || province) return
    const p = (provinceOptions || []).find((item) => item.label && containsCn(item.label, historyProvince))
    if (p) {
      setHistoryProvince(undefined)
      setProvinceId(p.value)
      onProvinceChange(p.label)
    }
  }, [provinceOptions, historyProvince, province, isMainland, onProvinceChange])

  // 城市预填依赖省的 cityOptions（省选好后接口才有数据）
  useEffect(() => {
    if (!isMainland || !historyCity || city) return
    const c = (cityOptions || []).find((item) => item.label && containsCn(item.label, historyCity))
    if (c) {
      setHistoryCity(undefined)
      setCityId(c.value)
      onCityChange(c.label)
    }
  }, [cityOptions, historyCity, city, isMainland, onCityChange])

  const onProvinceOk = (stateId) => {
    setProvinceId(stateId)
    // PC 分支走原生 select，value 为字符串（"225"），与选项数字 value 需统一后比较
    const selected = (provinceOptions || []).find((p) => String(p.value) === String(stateId))
    onProvinceChange(selected ? selected.label : undefined)
    setCityId(undefined)
    setHistoryCity(undefined)
    onCityChange(undefined)
    if (provinceError) setProvinceError('')
    if (cityError) setCityError('')
  }

  const onCityOk = (areaId) => {
    const selected = (cityOptions || []).find((c) => String(c.value) === String(areaId))
    setCityId(areaId)
    onCityChange(selected ? selected.label : undefined)
    if (cityError) setCityError('')
  }

  return (
    <List renderHeader={formatMessage({ id: 'addressForm-add' })}>
      {loading ? (
        <Loading />
      ) : (
        <>
          {isMainland ? (
            <>
              <PickerComponent
                title={formatMessage({ id: 'setMobile-chooseRegion' })}
                data={provinceOptions || []}
                onOk={onProvinceOk}
                value={provinceId}
              >
                <FormattedMessage id="addressForm-province" />
              </PickerComponent>
              {/* 市区选择器常驻展示（未选省时禁用）；直辖市 cities 接口返回区级条目（如 110101 东城区）；
                  所有省市必选市区（收货地址组成部分），后端按省市归一化运费档 */}
              <PickerComponent
                title={formatMessage({ id: 'setMobile-chooseRegion' })}
                data={cityOptions || []}
                onOk={onCityOk}
                value={cityId}
                disabled={!province}
              >
                <FormattedMessage id="addressForm-city" />
              </PickerComponent>
              {provinceError && <div className={styles['field-error']}>{provinceError}</div>}
              {cityError && <div className={styles['field-error']}>{cityError}</div>}
            </>
          ) : (
            <>
              <PickerComponent
                title={formatMessage({ id: 'setMobile-chooseRegion' })}
                data={regionOptions}
                onOk={handleRegionChange}
                value={addressType}
              >
                <FormattedMessage id="addressForm-region" />
              </PickerComponent>
              {addressTypeError && <div className={styles['field-error']}>{addressTypeError}</div>}
            </>
          )}
          <InputItem
            placeholder={formatMessage({ id: 'addressForm-addressDetail' })}
            value={address}
            onChange={(value) => {
              if (addressError) setAddressError('')
              setAddress(value)
            }}
            maxLength={100}
          >
            <FormattedMessage id="addressForm-address" />
          </InputItem>
          {addressError && <div className={styles['field-error']}>{addressError}</div>}
          <InputItem
            placeholder={formatMessage({ id: 'addressForm-defaultName' })}
            value={userName}
            maxLength={50}
            onChange={(value) => {
              if (userNameError) setUserNameError('')
              setUserName(value)
            }}
          >
            <FormattedMessage id="addressForm-contact" />
          </InputItem>
          {userNameError && <div className={styles['field-error']}>{userNameError}</div>}
          <InputItem
            placeholder={formatMessage({ id: 'setMobile-mobileInput' })}
            value={userMobile}
            maxLength={isMainland ? 11 : 50}
            type={isMainland ? 'phone' : 'text'}
            onChange={(value) => {
              if (mobileError) setMobileError('')
              setUserMobile(isMainland ? value.replace(/\D/g, '').slice(0, 11) : value)
            }}
          >
            <FormattedMessage id="addressForm-mobile" />
          </InputItem>
          {mobileError && <div className={styles['field-error']}>{mobileError}</div>}
        </>
      )}
    </List>
  )
}
